/**
 * Display Post List — cursor-following thumbnail modal + vertical reel.
 *
 * Mirrors the PoC in /var/www/cowork/alehop/landing-test/src/categorias.js
 * (lines 70-156). One init() per .wp-block-bskr-display-post-list root, so
 * multiple instances on the same page don't share state.
 */

import gsap from "gsap";

function initBlock(root) {
  const list   = root.querySelector(".bskr-display-post-list__list");
  const reel   = root.querySelector(".bskr-display-post-list__reel");
  const modal  = root.querySelector(".bskr-display-post-list__modal");
  const cursor = root.querySelector(".bskr-display-post-list__cursor");
  if (!list || !reel || !modal) return;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const fineHover     = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  // Only the modal follows the pointer. The "Ver" button lives inside the
  // modal (centred via CSS), so it stays attached to the image and inherits
  // its position + scale — no separate tween.
  const FOLLOW = { duration: 0.4, ease: "power3" };
  const xModal = gsap.quickTo(modal, "left", FOLLOW);
  const yModal = gsap.quickTo(modal, "top",  FOLLOW);

  gsap.set(modal, { scale: 0, xPercent: -50, yPercent: -50 });

  // Pointer tracking is only armed while the cursor is inside the section
  // expanded by THRESHOLD px. Outside that zone we tear the mousemove listener
  // down entirely, so scrolling/reading the rest of the page costs nothing —
  // no quickTo tweens fire when the modal can't possibly be visible.
  const THRESHOLD = 160;

  let rect = null;
  const refreshRect = () => { rect = root.getBoundingClientRect(); };

  let tracking = false;

  // Cached, viewport-relative bounds; refreshed on scroll/resize while armed.
  const outsideZone = (x, y) =>
    !rect ||
    x < rect.left   - THRESHOLD ||
    x > rect.right  + THRESHOLD ||
    y < rect.top    - THRESHOLD ||
    y > rect.bottom + THRESHOLD;

  function stopTracking() {
    if (!tracking) return;
    tracking = false;
    window.removeEventListener("mousemove", track);
    window.removeEventListener("scroll", refreshRect);
    window.removeEventListener("resize", refreshRect);
  }

  function track(e) {
    xModal(e.clientX);
    yModal(e.clientY);
    if (outsideZone(e.clientX, e.clientY)) stopTracking();
  }

  function startTracking(e) {
    if (tracking || !fineHover || reducedMotion) return;
    tracking = true;
    refreshRect();
    if (e) { xModal(e.clientX); yModal(e.clientY); }
    window.addEventListener("mousemove", track, { passive: true });
    window.addEventListener("scroll", refreshRect, { passive: true });
    window.addEventListener("resize", refreshRect, { passive: true });
  }

  // mouseenter on the section re-arms tracking the moment the cursor touches
  // it. Combined with the THRESHOLD exit margin this gives hysteresis: arm on
  // contact, disarm only after leaving the section by THRESHOLD px.
  if (fineHover && !reducedMotion) {
    root.addEventListener("mouseenter", startTracking);
  }

  let isOpen = false;

  function open() {
    if (!fineHover || reducedMotion || isOpen) return;
    isOpen = true;
    gsap.to(modal, { scale: 1, duration: 0.4, ease: "expo.out", transformOrigin: "center" });
    list.dataset.hovering = "true";
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    gsap.to(modal, { scale: 0, duration: 0.35, ease: "expo.inOut" });
    list.dataset.hovering = "false";
  }

  // Reel uses height 100% per slide stacked vertically; moving `top` by
  // -N*100% reveals the Nth thumbnail.
  function setIndex(i) {
    reel.style.top = `${i * -100}%`;
  }

  // mouseenter/leave on the <ul> avoids flicker when the cursor crosses
  // gaps between rows — keeps the modal open while inside the list.
  list.addEventListener("mouseenter", open);
  list.addEventListener("mouseleave", close);

  Array.from(list.children).forEach((row, i) => {
    row.addEventListener("mouseenter", () => {
      setIndex(i);
      // Mirror the hovered row's title in the floating cursor pill.
      if (cursor) {
        const title = row.querySelector(".bskr-display-post-list__title");
        if (title) cursor.textContent = title.textContent.trim();
      }
    });
  });
}

function init() {
  document.querySelectorAll(".wp-block-bskr-display-post-list").forEach(initBlock);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
